import { Component, ErrorInfo, ReactNode } from 'react'
import { NavigateFunction } from 'react-router-dom'

interface Props {
  children: ReactNode
  navigate: NavigateFunction
}

interface State {
  hasError: boolean
  error: Error | null
}

/**
 * Error boundary that catches Convex errors and redirects to login
 * when 2FA verification is required.
 */
class ConvexErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ConvexErrorBoundary caught error:', error, errorInfo)

    // Check if this is a 2FA verification error
    const is2FAError =
      error.message?.includes('2FA verification required') ||
      error.message?.includes('Not authenticated')

    if (is2FAError) {
      console.log('2FA session expired, redirecting to login...')
      // Redirect to login page
      this.props.navigate('/admin/login', { replace: true })
    }
  }

  public componentDidUpdate(_prevProps: Props, prevState: State) {
    // Reset error state when navigation changes
    if (this.state.hasError && prevState.hasError) {
      // If we've navigated away, reset the error state
      this.setState({ hasError: false, error: null })
    }
  }

  public render() {
    if (this.state.hasError) {
      const is2FAError =
        this.state.error?.message?.includes('2FA verification required') ||
        this.state.error?.message?.includes('Not authenticated')

      if (is2FAError) {
        // Return null while redirecting
        return null
      }

      // For other errors, show a fallback UI
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-white p-8">
          <div className="max-w-md text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">
              Something went wrong
            </h1>
            <p className="text-gray-700 mb-4">
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null })
                window.location.reload()
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Reload Page
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ConvexErrorBoundary
